import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Monitor } from "lucide-react";
import { formatErr } from "@/lib/api";
import { desk } from "@/pages/admin/desktop/deskApi";
import { DesktopGoogleButton, startDesktopGoogle } from "./DesktopGoogleButton";

export default function DesktopActivatePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [loginEmail, setLoginEmail] = useState(null); // null=checking, false=bad token
  const [businessName, setBusinessName] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setLoginEmail(false); return; }
    desk.get(`/desktop/auth/invite/${token}`)
      .then((r) => setLoginEmail(r.data.login_email))
      .catch(() => setLoginEmail(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== password2) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await desk.post("/desktop/auth/activate", {
        token, business_name: businessName, business_email: businessEmail, password,
      });
      localStorage.setItem("desktop_token", data.token);
      navigate("/desktop", { replace: true });
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "radial-gradient(120% 90% at 20% 10%, #2563eb 0%, #1e3a8a 38%, #0f172a 100%)" }}>
      <div className="w-full max-w-md bg-white/95 backdrop-blur rounded-xl shadow-2xl p-6" data-testid="desk-activate-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
            <Monitor className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Activate your Desktop</h1>
            {loginEmail && <p className="text-xs text-slate-500">Signing in as <strong>{loginEmail}</strong></p>}
          </div>
        </div>
        {loginEmail === null && <p className="text-sm text-slate-400 text-center py-6">Checking your link…</p>}
        {loginEmail === false && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-3 text-center" data-testid="desk-activate-bad-token">
            This activation link is invalid or was already used.
          </p>
        )}
        {loginEmail && (
          <div className="space-y-4">
            <DesktopGoogleButton onClick={() => startDesktopGoogle(token)} disabled={busy} testId="desk-activate-google" />
            <div className="flex items-center gap-3" aria-hidden="true"><span className="h-px flex-1 bg-slate-200" /><span className="text-[11px] font-semibold uppercase text-slate-400">or create a password</span><span className="h-px flex-1 bg-slate-200" /></div>
          <form onSubmit={submit} className="space-y-3">
            {err && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="desk-activate-error">{err}</p>}
            <div>
              <label className="text-xs font-semibold text-slate-600">Business name</label>
              <input required value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                placeholder="e.g. Smith Consulting LLC"
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="desk-activate-business-name" />
              <p className="text-[11px] text-slate-400 mt-0.5">People you send documents to will see this name.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Business email</label>
              <input type="email" required value={businessEmail} onChange={(e) => setBusinessEmail(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="desk-activate-business-email" />
              <p className="text-[11px] text-slate-400 mt-0.5">Replies to your signature emails go here.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Password</label>
              <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="desk-activate-password" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Confirm password</label>
              <input type="password" required value={password2} onChange={(e) => setPassword2(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="desk-activate-password2" />
            </div>
            <button type="submit" disabled={busy}
              className="w-full bg-sky-600 hover:bg-sky-700 text-white font-bold text-sm rounded-md py-2.5 disabled:opacity-60"
              data-testid="desk-activate-submit">
              {busy ? "Activating…" : "Activate & open my Desktop"}
            </button>
          </form>
          </div>
        )}
      </div>
    </div>
  );
}
